/* eslint-disable @typescript-eslint/strict-boolean-expressions */
// TODO take this file out of contexts and put it in a more appropriate place
// this file was created to use with FormContext which we are not using anymore
import { hasQualifiedStatus } from '@common/order/helperFunctions';
import { lineItemSchemaFactory } from '@common/order/validations';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { getOrderCarrierMaxRateBlock } from '@store/orders/selectors';
import {
  computeTotalLineItems,
  isCreditLimitExceeded,
  isNegativeCreditLiabilityAccepted,
  isOrderInNegativeCreditWarningZone,
  orderStore,
} from '@store/orders';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { createConditionalSchema } from '@utils/formUtils';
import { compact, flatten } from 'lodash';
import * as yup from 'yup';

import { adjustmentsSchema } from './adjustmentSchema';
import {
  CARRIER_TOTAL_EXCEEDS_MAX_RATE_BOOKED_MESSAGE,
  CARRIER_TOTAL_EXCEEDS_MAX_RATE_MESSAGE,
  reeferEquipmentTypes,
} from './constants';
import {
  checkLTLModeError,
  checkTLModeError,
  // createLineItemTypeSchema,
  isStopValid,
  ltlLoadHasItemDescription,
} from './helperFunctions';
import { vendorsArraySchema } from './vendorsSchema';

/* Use this Phone Regex for validating phone number in project.  */
const phoneRegExp = /^\(\d{3}\) \d{3}-\d{4}$/;

const flagSchema = yup.object().shape({
  description: yup.string().nullable(),
  flagType: yup.string().nullable(),
  emailId: yup.string().nullable(),
});

const freightValueSchema = (cargoValueIsHigh: boolean) => {
  if (cargoValueIsHigh) {
    return yup
      .number()
      .min(100001, 'Cargo Value must be $100,001 - $999,999,999')
      .max(999999999, 'Cargo Value must be $100,001 - $999,999,999')
      .required('Cargo Value is required')
      .meta({
        section: 'carrier',
      });
  }

  return yup
    .number()
    .nullable()
    .min(0, 'Cargo Value must be $0 - $100,000')
    .max(100000, 'Cargo Value must be $0 - $100,000')
    .meta({
      section: 'carrier',
    });
};

export const loadsSchema = yup.object().shape({
  cargoValueIsHigh: yup.string().nullable(),
  freightValue: yup
    .number()
    .nullable()
    .when('cargoValueIsHigh', ([cargoValueIsHigh], schema) => {
      return schema.test({
        name: 'freightValue',
        test: function (value) {
          const load = this.parent as ILoadEditViewModel;
          const mode = load.mode;

          if (mode === 'Partial') {
            return true; // Skip validation if mode is 'Partial'
          }

          try {
            freightValueSchema(cargoValueIsHigh === 'Yes').validateSync(value);
            return true;
          } catch (error) {
            // @ts-expect-error this will be known
            return this.createError({ message: error.message });
          }
        },
      });
    }),
  minPrice: yup.number().nullable(),
  maxPrice: yup.number().nullable(),
  maxRateError: yup
    .string()
    .nullable()
    .test({
      name: 'maxRateError',
      test() {
        const bookingStage = getOrderCarrierMaxRateBlock(
          this.parent as ILoadEditViewModel,
        );
        if (bookingStage === null) {
          return true;
        }
        return this.createError({
          message:
            bookingStage === 'booked'
              ? CARRIER_TOTAL_EXCEEDS_MAX_RATE_BOOKED_MESSAGE
              : CARRIER_TOTAL_EXCEEDS_MAX_RATE_MESSAGE,
        });
      },
    })
    .meta({ section: 'carrier' }),
  stopsCustomError: yup.string().when(() => {
    return yup
      .string()
      .nullable()
      .test({
        name: 'stopsCustomError',
        test: function () {
          const { createError, options, parent } = this;
          const { index } = options as { index: number };
          const load = parent as ILoadEditViewModel;

          const stopErr = isStopValid(load.stops ?? []);
          const TLModeError =
            load.mode === 'TL' ? checkTLModeError(load, index) : [];
          const LTLModeError =
            load.mode === 'LTL' ? checkLTLModeError(load) : [];
          const missingItemDescriptionError =
            load.mode === 'LTL' && !ltlLoadHasItemDescription(load)
              ? ['Stop Items are missing an Item Description.']
              : [];

          const errors = compact(
            flatten([
              stopErr,
              TLModeError,
              LTLModeError,
              missingItemDescriptionError,
            ]),
          );

          if (errors.length > 0) {
            return createError({
              message: errors as unknown as yup.Message,
            });
          }
          return true;
        },
      })
      .meta({ section: 'carrier' });
  }),
  negativeCreditLimitError: yup
    .string()
    .nullable()
    .test({
      name: 'negativeCreditLimitError',
      test() {
        const { createError, parent } = this;
        const load = parent as ILoadEditViewModel;

        if (
          !isFeatureFlagEnabled(
            FeatureFlags.feature_order_block_save_on_neg_credit_limit,
          )()
        ) {
          return true;
        }

        if (orderStore.isCreate) {
          return true;
        }

        if (!hasQualifiedStatus(load.status) || !load.carrierId) {
          return true;
        }

        // Liability acceptance unblocks save when the order is in the
        // negative credit warning zone or has exceeded the credit limit.
        if (isNegativeCreditLiabilityAccepted()) {
          return true;
        }

        if (isCreditLimitExceeded() && load.mode !== 'LTL') {
          return createError({
            message: 'Negative credit limit liability must be accepted',
          });
        }

        if (isOrderInNegativeCreditWarningZone() && load.mode !== 'LTL') {
          return createError({
            message: 'Negative credit limit liability must be accepted',
          });
        }

        return true;
      },
    })
    .meta({ section: 'loads' }),
  driver: yup
    .string()
    .max(50, 'Driver name must be less than 50 characters')
    .nullable(),
  driverPhoneNumber: yup
    .string()
    .trim()
    .transform((value: string | null) =>
      value === '' ? null : (value?.trim() ?? value),
    )
    .nullable()
    .length(10, 'Phone number must be exactly 10 digits'),
  trailerNumber: yup
    .string()
    .max(50, 'Trailer number must be less than 50 characters')
    .nullable(),
  vinNumber: yup
    .string()
    .max(50, 'VIN number must be less than 50 characters')
    .nullable(),
  truckNumber: yup
    .string()
    .max(50, 'Truck number must be less than 50 characters')
    .nullable(),
  lineItems: yup
    .array()
    .of(createConditionalSchema(lineItemSchemaFactory('instructionsAndCosts'))),
  flags: yup.array().of(flagSchema),
  nextCheckCallDateUtc: yup.string().nullable(),
  tempInstructions: yup
    .string()
    .max(1000, 'Instructions must be less than 1000 characters')
    .when(
      [
        'equipment',
        'minTemperature',
        'maxTemperature',
        'temperatureUnit',
        'runMode',
      ],
      (
        [equipment, minTemperature, maxTemperature, temperatureUnit, runMode],
        rule,
      ) => {
        const hasOneOfRequiredFields =
          !!maxTemperature ||
          !!minTemperature ||
          !!temperatureUnit ||
          !!runMode;

        if (
          !!equipment &&
          reeferEquipmentTypes.includes(equipment as string) &&
          !hasOneOfRequiredFields
        ) {
          return rule.required(
            'Instructions are required if the top section is empty',
          );
        }

        return rule.nullable();
      },
    ),
  minTemperature: yup
    .number()
    .min(-100, 'Min temperature should not be less than -100')
    .max(100, 'Min temperature should not be more than 100')
    .when('equipment', ([equipment], rule) => {
      if (!!equipment && reeferEquipmentTypes.includes(equipment as string)) {
        return yup.lazy((item, options) => {
          const load: ILoadEditViewModel = options.parent;
          const tempInstructions = load.tempInstructions;
          const maxTemperature = load.maxTemperature;
          const temperatureUnit = load.temperatureUnit;
          const runMode = load.runMode;
          const hasOneOfRequiredFields =
            !!maxTemperature || !!temperatureUnit || !!runMode;

          if (!tempInstructions || hasOneOfRequiredFields) {
            return rule.required('A min temperature is required')!;
          }

          return rule.notRequired();
        });
      }

      return rule.nullable();
    }),
  maxTemperature: yup
    .number()
    .min(-100, 'Max temperature should not be less than -100')
    .max(100, 'Max temperature should not be more than 100')
    .when('equipment', ([equipment], rule) => {
      if (!!equipment && reeferEquipmentTypes.includes(equipment as string)) {
        return yup.lazy((item, options) => {
          const load: ILoadEditViewModel = options.parent;
          const tempInstructions = load.tempInstructions;
          const minTemperature = load.minTemperature;
          const temperatureUnit = load.temperatureUnit;
          const runMode = load.runMode;
          const hasOneOfRequiredFields =
            !!minTemperature || !!temperatureUnit || !!runMode;

          if (!tempInstructions || hasOneOfRequiredFields) {
            return rule.required('A max temperature is required')!;
          }

          return rule.notRequired();
        });
      }

      return yup.number().nullable();
    }),
  temperatureUnit: yup
    .mixed()
    .oneOf(['Fahrenheit', 'Celsius'])
    .when('equipment', ([equipment], rule) => {
      if (!!equipment && reeferEquipmentTypes.includes(equipment as string)) {
        return yup.lazy((item, options) => {
          const load: ILoadEditViewModel = options.parent;
          const tempInstructions = load.tempInstructions;
          const minTemperature = load.maxTemperature;
          const maxTemperature = load.minTemperature;
          const runMode = load.runMode;
          const hasOneOfRequiredFields =
            !!minTemperature || !!maxTemperature || !!runMode;

          if (!tempInstructions || hasOneOfRequiredFields) {
            return rule.required('A unit of measure is required')!;
          }

          return rule.notRequired();
        });
      }

      return rule.nullable();
    }),
  runMode: yup
    .mixed()
    .oneOf(['Continuous', 'Cycle'])
    .when('equipment', ([equipment], rule) => {
      if (!!equipment && reeferEquipmentTypes.includes(equipment as string)) {
        return yup.lazy((item, options) => {
          const load: ILoadEditViewModel = options.parent;
          const tempInstructions = load.tempInstructions;
          const minTemperature = load.maxTemperature;
          const maxTemperature = load.minTemperature;
          const temperatureUnit = load.temperatureUnit;
          const hasOneOfRequiredFields =
            !!minTemperature || !!maxTemperature || !!temperatureUnit;

          if (!tempInstructions || hasOneOfRequiredFields) {
            return rule.required('A run mode is required')!;
          }

          return rule.notRequired();
        });
      }

      return rule.nullable();
    }),
});

export const orderFormSchema = yup.object({
  customerId: yup.number().when((value) => {
    return value[0] === null
      ? yup
          .number()
          .required('Customer is required')
          .meta({ section: 'customerInformation' })
      : yup.number().nullable();
  }),
  status: yup.string(),
  referenceNumber: yup
    .string()
    .nullable()
    .max(120, 'Reference Number is too long'),
  bidType: yup
    .string()
    .required('A bid type is required')
    .meta({ section: 'customerInformation' }),
  spotId: yup
    .string()
    .nullable()
    .max(50, 'Spot Id must be less than 50 characters'),
  spotQuote: yup
    .mixed()
    .nullable()
    .test('is-number', 'Spot Quote must be a number', (value: unknown) => {
      if (value === null || value === '' || value === undefined) {
        return true;
      }
      return !isNaN(Number(value));
    })
    .test(
      'max-value',
      'Spot Quote can not be more than 99999.99',
      (value: unknown) => {
        if (value === null || value === '' || value === undefined) {
          return true;
        }
        return Number(value) <= 99999.99;
      },
    ),
  ownner: yup.string(),
  driverPhonext: yup
    .string()
    .nullable()
    .matches(phoneRegExp, {
      message: 'Phone number is not valid',
      excludeEmptyString: true,
    })
    .meta({ section: 'carrierDriverDetails' }),
  loadTenderId: yup.number().nullable(),
  currencyType: yup.string(),
  carrierInstructions: yup.string(),
  legalText: yup.string(),
  lineItems: yup
    .array()
    .of(createConditionalSchema(lineItemSchemaFactory('customerInformation')))
    .test(
      'non-negative-total',
      'Rate changes cannot result in a negative total amount.',
      () => {
        const total = computeTotalLineItems();
        return !Number.isFinite(total) || total >= 0;
      },
    )
    .meta({ section: 'customerInformation' }),
  loads: yup.array().of(createConditionalSchema(loadsSchema)),
  vendors: vendorsArraySchema,
  // cargoValue: yup.string(),
  holdRetraction: yup.boolean().nullable(),
  exceptionReason: yup
    .string()
    .when('holdRetraction', ([holdRetraction]) =>
      holdRetraction === true
        ? yup
            .string()
            .max(500)
            .required('Exception Reason is required')
            .meta({ section: 'customerInformation' })
        : yup.string().max(500).nullable(),
    ),
  revisitRetractionDate: yup
    .string()
    .when('holdRetraction', ([holdRetraction]) =>
      holdRetraction === true
        ? yup
            .string()
            .required('Revisit Retraction Hold Date is required')
            .meta({ section: 'customerInformation' })
        : yup.string().nullable(),
    ),
  adjustments: yup.array().of(adjustmentsSchema),
  badAdjustments: yup.string().when(() => {
    return yup
      .string()
      .nullable()
      .test({
        name: 'badAdjustments',
        test: function () {
          const order: IOrderViewModel = this.parent;
          const adjError = order.adjustments.some((adj) => {
            const retract =
              adj.amount > 0 && (adj.retraction === 1 || adj.retraction === 2);
            const refund = adj.amount < 0 && adj.retraction === 3;
            return retract || refund;
          });
          if (adjError) {
            return this.createError({
              message:
                'There is an adjustment with a bad type and amount combination. Please check your adjustments and try again.',
            });
          }
          return true;
        },
      })
      .meta({ section: 'customerInformation' });
  }),
});
